import React from "react";
import { DailyTip } from "@/features/daily/components/DailyTip";
import { StatTile } from "@/features/daily/components/StatTile";
import { statTileViewModel } from "@/features/daily/components/stat-tile-view-model";
import {
    ActionVm,
    DeltaVm,
    GrowthRowVm,
    KpiGroupVm,
    RiskVm,
} from "@/features/daily/components/executive/legacy-view-model";

const Delta: React.FC<{ delta: DeltaVm | null }> = ({ delta }) =>
    delta === null ? (
        <span className="daily-delta" data-tone="neutral">—</span>
    ) : (
        <span className="daily-delta" data-tone={statTileViewModel.deltaTone(delta)}>
            <span aria-hidden="true">{statTileViewModel.arrow(delta.direction)}</span> {delta.label}
        </span>
    );

/**
 * D115 + tiles: eight identical stat tiles in one row, under four thin group headers
 * that never change the tiles' height.
 */
export const KpiGroups: React.FC<{ groups: KpiGroupVm[] }> = ({ groups }) => (
    <div className="daily-kpi-groups" aria-label="Indicadores ejecutivos">
        {groups.map((group) => (
            <section key={group.key} className="daily-kpi-group" aria-label={group.label} data-group={group.key}>
                <h2 className="daily-kpi-group__title">{group.label}</h2>
                <ul className="daily-tiles daily-kpi-group__cards">
                    {group.cards.map((card) => <StatTile key={card.key} tile={card} idPrefix="daily-kpi-tip" />)}
                </ul>
            </section>
        ))}
    </div>
);

/** D111 · B: current vs previous per metric. */
export const GrowthSummary: React.FC<{ rows: GrowthRowVm[] }> = ({ rows }) => (
    <section className="daily-panel daily-growth" aria-labelledby="daily-growth-title">
        <div className="daily-panel__head">
            <h2 id="daily-growth-title">B. Resumen de crecimiento</h2>
            <span className="daily-small daily-muted">frente al periodo anterior</span>
        </div>
        <div className="daily-table__scroll daily-panel__scroll" role="region" aria-label="Resumen de crecimiento, lista desplazable" tabIndex={0}>
            <table className="daily-table daily-table--wrap">
                <caption className="daily-visually-hidden">Resumen de crecimiento frente al periodo anterior</caption>
                <thead>
                    <tr>
                        <th scope="col">Métrica</th>
                        <th scope="col" className="is-numeric">Actual</th>
                        <th scope="col" className="is-numeric">Anterior</th>
                        <th scope="col" className="is-numeric">Cambio</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={4} className="daily-table__empty">Sin datos del periodo.</td></tr>
                    ) : rows.map((row) => (
                        <tr key={row.key} className={row.secondary ? "daily-growth__secondary" : undefined}>
                            <th scope="row">{row.metric}</th>
                            <td className="is-numeric">{row.currentLabel}</td>
                            <td className="is-numeric">{row.previousLabel}</td>
                            <td className="is-numeric"><Delta delta={row.change} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </section>
);

/**
 * D111 · C + D132: legacy density — one table row per risk (Riesgo · Actual · Severidad ·
 * Por qué importa); every cell wraps, nothing is cut with an ellipsis.
 */
export const RisksList: React.FC<{ risks: RiskVm[] }> = ({ risks }) => (
    <section className="daily-panel daily-risks" aria-labelledby="daily-risks-title">
        <div className="daily-panel__head">
            <div className="daily-trend__title">
                <h2 id="daily-risks-title">C. Riesgos ejecutivos</h2>
                {/* D143: the severity legend that used to sit under the page. */}
                <DailyTip
                    id="daily-risks-legend"
                    label="Leyenda de severidad"
                    lines={["Severidad, de mayor a menor: Crítica · Alta · Media · Baja.", "El color repite la palabra; nunca la sustituye."]}
                />
            </div>
            <span className="daily-small daily-muted">
                {risks.length === 0 ? "Sin riesgos activos" : risks.length === 1 ? "1 activo" : `${risks.length} activos`}
            </span>
        </div>
        <div className="daily-panel__scroll" role="region" aria-label="Riesgos ejecutivos, lista desplazable" tabIndex={0}>
            {risks.length === 0 ? (
                <p className="daily-small daily-muted">La plataforma está dentro de objetivo en las dimensiones que se siguen.</p>
            ) : (
                <table className="daily-table daily-table--wrap daily-risk-table">
                    <caption className="daily-visually-hidden">Riesgos ejecutivos del periodo</caption>
                    <thead>
                        <tr>
                            <th scope="col">Riesgo</th>
                            <th scope="col">Actual</th>
                            <th scope="col">Severidad</th>
                            <th scope="col">Por qué importa</th>
                        </tr>
                    </thead>
                    <tbody>
                        {risks.map((risk) => (
                            <tr key={risk.id} className="daily-risk" data-tone={risk.severity.tone}>
                                <th scope="row">{risk.title}</th>
                                <td className="daily-risk__value">{risk.currentLabel}</td>
                                <td><span className="daily-pill" data-tone={risk.severity.tone}>{risk.severity.label}</span></td>
                                <td className="daily-risk__why">{risk.why}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    </section>
);

/** D111 · D: numbered cards — impact, evidence, primary impact and the action to take. */
export const ActionsList: React.FC<{ actions: ActionVm[] }> = ({ actions }) => (
    <section className="daily-panel daily-actions-panel" aria-labelledby="daily-actions-title">
        <div className="daily-panel__head">
            <h2 id="daily-actions-title">D. Acciones recomendadas</h2>
            <span className="daily-small daily-muted">
                {actions.length === 0 ? "Ninguna en cola" : `${actions.length} priorizadas`}
            </span>
        </div>
        <div className="daily-panel__scroll" role="region" aria-label="Acciones recomendadas, lista desplazable" tabIndex={0}>
            {actions.length === 0 ? (
                <p className="daily-small daily-muted">Nada requiere intervención ejecutiva ahora mismo.</p>
            ) : (
                <ol className="daily-action-list">
                    {actions.map((action, index) => (
                        <li key={action.id} className="daily-action">
                            <span className="daily-action__rank" aria-hidden="true">{index + 1}</span>
                            <div className="daily-action__body">
                                <div className="daily-risk__top">
                                    <h3 className="daily-risk__title">{action.title}</h3>
                                    <span className="daily-pill" data-tone={action.impact.tone}>Impacto · {action.impact.label}</span>
                                </div>
                                {action.evidence.length === 0 ? null : (
                                    <ul className="daily-action__evidence" aria-label="Evidencia">
                                        {action.evidence.map((item) => <li key={item} className="daily-chip">{item}</li>)}
                                    </ul>
                                )}
                                <p className="daily-small"><strong>Impacto principal:</strong> {action.primaryImpact}</p>
                                <p className="daily-small"><strong>Acción:</strong> {action.action}</p>
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    </section>
);
